import React, { useEffect, useState } from "react";
import moment from "moment";
import { getPmSessionMonthRangeStrings } from "helpers/pmSessionDateHelper";
import { useCptFilterLoading } from "../common/useCptFilterLoading";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

const YEARS = Array.from({ length: 5 }, (_, i) => (moment().year() - i).toString());

const buildDateRange = (month, year) => {
  const { startDate, endDate } = getPmSessionMonthRangeStrings(year, month);
  return {
    startDate,
    endDate,
    selectedMonth: month,
    selectedYear: year
  };
};

const NonAdherentFilters = ({
  selectedMonth,
  selectedYear,
  patientStatusFilter,
  careProviderFilter,
  clinicFilter,
  careProviderOptions,
  clinicOptions,
  onApplyApiFilters,
  onLocalFilterChange,
  loading = false
}) => {
  const { isFilterBusy, beginSubmit } = useCptFilterLoading(loading);
  const [month, setMonth] = useState(selectedMonth || moment().format("MM"));
  const [year, setYear] = useState(selectedYear || moment().format("YYYY"));
  const [status, setStatus] = useState(patientStatusFilter || "all");
  const [provider, setProvider] = useState(careProviderFilter || "all");
  const [clinic, setClinic] = useState(clinicFilter || "all");
  const [validationMessage, setValidationMessage] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);

  useEffect(() => {
    setMonth(selectedMonth || moment().format("MM"));
  }, [selectedMonth]);

  useEffect(() => {
    setYear(selectedYear || moment().format("YYYY"));
  }, [selectedYear]);

  useEffect(() => {
    setStatus(patientStatusFilter || "all");
  }, [patientStatusFilter]);

  useEffect(() => {
    setProvider(careProviderFilter || "all");
  }, [careProviderFilter]);

  useEffect(() => {
    setClinic(clinicFilter || "all");
  }, [clinicFilter]);

  useEffect(() => {
    if (onLocalFilterChange) {
      onLocalFilterChange({
        careProviderFilter: provider,
        clinicFilter: clinic
      });
    }
  }, [provider, clinic, onLocalFilterChange]);

  const handleApply = () => {
    const selectedDate = moment(`${year}-${month}-01`);
    if (selectedDate.isAfter(moment(), "month")) {
      setValidationMessage("Future month is not allowed.");
      setFilterApplied(false);
      return;
    }

    setValidationMessage("");
    setFilterApplied(true);
    beginSubmit();
    if (onApplyApiFilters) {
      onApplyApiFilters({
        patientStatusFilter: status,
        ...buildDateRange(month, year)
      });
    }
  };

  const handleReset = () => {
    const currentMonth = moment().format("MM");
    const currentYear = moment().format("YYYY");
    setMonth(currentMonth);
    setYear(currentYear);
    setStatus("all");
    setProvider("all");
    setClinic("all");
    setValidationMessage("");
    setFilterApplied(false);
    beginSubmit();

    if (onApplyApiFilters) {
      onApplyApiFilters({
        patientStatusFilter: "all",
        ...buildDateRange(currentMonth, currentYear)
      });
    }
  };

  const selectedMonthLabel = MONTHS.find((item) => item.value === month)?.label || "";
  const displayMonth = `${selectedMonthLabel} ${year}`;

  return (
    <div className="cpt-filter-wrapper">
      <div className={`mpr-filter-card ${isFilterBusy ? "mpr-filter-card--loading" : ""}`}>
        <div className="mpr-filter-header">
          <div>
            <h4>Adherence/Non-Adherence Months Report</h4>
            <p>Select filters and apply to load adherence/non-adherence analytics.</p>
          </div>
        </div>
        <div className="mpr-filter-grid na-simple-filter-grid">
          <div className="mpr-filter-field">
            <label>Month</label>
            <select className="mpr-select" value={month} onChange={(e) => setMonth(e.target.value)} disabled={isFilterBusy}>
              {MONTHS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div className="mpr-filter-field">
            <label>Year</label>
            <select className="mpr-select" value={year} onChange={(e) => setYear(e.target.value)} disabled={isFilterBusy}>
              {YEARS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="mpr-filter-field">
            <label>Adherence Types</label>
            <select className="mpr-select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={isFilterBusy}>
              <option value="all">All </option>
              <option value="adherent">Adherent</option>
              <option value="non-adherent">Non-Adherent</option>
            </select>
          </div>
          <div className="mpr-filter-field">
            <label>Care Provider</label>
            <select className="mpr-select" value={provider} onChange={(e) => setProvider(e.target.value)} disabled={isFilterBusy}>
              {careProviderOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div className="mpr-filter-field">
            <label>Clinic</label>
            <select className="mpr-select" value={clinic} onChange={(e) => setClinic(e.target.value)} disabled={isFilterBusy}>
              {clinicOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </div>
          <div className="mpr-filter-actions na-simple-filter-actions">
            <button
              type="button"
              className={`mpr-apply-btn ${isFilterBusy ? "mpr-apply-btn--loading" : ""}`}
              onClick={handleApply}
              aria-busy={isFilterBusy}
            >
              {isFilterBusy ? <i className="mdi mdi-loading mdi-spin" /> : <i className="mdi mdi-filter" />}
              {isFilterBusy ? "Loading..." : "Filter"}
            </button>
            <button type="button" className="mpr-reset-btn" onClick={handleReset} disabled={isFilterBusy}>
              <i className="mdi mdi-refresh"></i>
              Reset
            </button>
          </div>
        </div>
        <div className="mpr-filter-caption">
          <span>{`Selected: ${displayMonth}`}</span>
          <span>{`Status: ${status}`}</span>
          {validationMessage ? <span className="text-danger">{validationMessage}</span> : null}
          {filterApplied && !validationMessage ? <span>Filter applied</span> : null}
        </div>
      </div>
    </div>
  );
};

export default NonAdherentFilters;
